import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  IconButton,
  Button,
  Chip,
  Paper,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  LinearProgress,
  CircularProgress,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  PhotoCamera as PhotoIcon,
  ZoomIn as ZoomIcon,
  MoreVert as MoreIcon,
  CompareArrows as CompareIcon,
  Download as DownloadIcon,
  TrendingUp as ProgressIcon,
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
  CloudUpload as UploadIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useConfirm } from "material-ui-confirm";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import "firebase/compat/storage";

import consola from "consola";

// Import our new custom hooks
import useClientPhotos from "../../../../../hooks/useClientPhotos";
import usePhotoAnalysis from "../../../../../hooks/usePhotoAnalysis";
import PhotoAnalysisReport from "../../../../../components/organisms/PhotoAnalysisReport";
import ProgressPhotoCompareAnyTwoDialog from "../../../../../components/organisms/ProgressPhotoCompareAnyTwoDialog";
import ProgressPhotoGroupPanel from "../../../../../components/organisms/ProgressPhotoGroupPanel";
import { groupFormattedPhotosIntoGroups } from "../../../../../utils/groupProgressPhotosForDisplay";
import { AIButton } from "../../../../../components";
import NewColors from "../../../../../themes/NewColors";
import useEntitlements from "../../../../../hooks/useEntitlements";
import UpgradePrompt from "../../../../../components/organisms/UpgradePrompt";
import ProgressPhotoUploadDialog from "../../../../../components/organisms/ProgressPhotoUploadDialog";
import ProgressPhotosPrivacyGate from "../../../../../components/molecules/ProgressPhotosPrivacyGate";
import organizationIdState from "../../../../../atoms/organizationIdSelector";
import { getRecoil } from "recoil-nexus";
import MonthYearPicker from "../../../../../components/molecules/MonthYearPicker";
import {
  getMonthRange,
  getMonthRangeFromKey,
} from "../../../../../utils/monthDateRange";

const TODAY_MONTH_KEY = getMonthRange(new Date()).key;
// Shared styled components (consistent with other tabs)
const StyledPhotosCard = styled(Card)(({ theme }) => ({
  height: "auto",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  transition: "box-shadow 0.3s ease",
  "&:hover": {
    boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
  },
}));

const PhotosHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const PhotosContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
  paddingTop: theme.spacing(2),
}));

const ActionButton = styled(Button)(() => ({
  borderRadius: 20,
  padding: "8px 20px",
  textTransform: "none",
  fontWeight: 600,
}));

// Enhanced Photos Tab Component
/** @param {boolean} [props.embedded] — when true (e.g. weekly report), skip outer padding so cards match full-width report sections */
const EnhancedPhotosTabContent = ({ client, photoUpdate, embedded = false }) => {
  // State management - use lazy initialization for objects
  const [selectedPhoto, setSelectedPhoto] = React.useState(null);
  const [openViewer, setOpenViewer] = React.useState(false);
  const [compareMode, setCompareMode] = React.useState(false);
  const [selectedPhotos, setSelectedPhotos] = React.useState([]);
  const [openCompareDialog, setOpenCompareDialog] = React.useState(false);
  // Add state for download progress
  const [downloading, setDownloading] = React.useState(false);
  const [downloadProgress, setDownloadProgress] = React.useState(0);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState("");
  const [snackbarSeverity, setSnackbarSeverity] = React.useState("info");
  // States for photo navigation
  const [currentGroupPhotos, setCurrentGroupPhotos] = React.useState([]);
  const [currentPhotoIndex, setCurrentPhotoIndex] = React.useState(0);

  // Photo analysis states
  const [showAnalysisReport, setShowAnalysisReport] = React.useState(false);
  const [analysisError, setAnalysisError] = React.useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = React.useState(false);

  const organizationId = React.useMemo(
    () => getRecoil(organizationIdState),
    [],
  );

  const confirm = useConfirm();
  const { hasFeature, organizationData } = useEntitlements();
  const canAnalyzePhotos = hasFeature("ai_progress_photo_analysis");

  // Use our custom hook with stable client ID reference
  const clientId = React.useMemo(() => client?.uid, [client?.uid]);

  const {
    startingPhotos,
    progressPhotos,
    loading,
    setStartingPhotoGroup,
    deleteProgressPhotos,
    formatPhotosForDisplay,
    getProgressPhotos,
    getStartingProgressPhotos,
    progressPhotoConfig,
  } = useClientPhotos(clientId);

  const [monthKey, setMonthKey] = React.useState(TODAY_MONTH_KEY);

  // Load progress photos for the selected month only (reference photos load separately)
  React.useEffect(() => {
    if (!clientId) return undefined;
    let mounted = true;
    const { start, end } = getMonthRangeFromKey(monthKey);
    const load = async () => {
      await getProgressPhotos(clientId, { start, end }, 50);
      if (!mounted) return;
    };
    load();
    return () => {
      mounted = false;
    };
  }, [clientId, monthKey, getProgressPhotos]);

  // Photo analysis hook
  const photoAnalysis = usePhotoAnalysis(clientId);

  // Format photos for display with stable references
  const formattedProgressPhotos = React.useMemo(() => {
    if (!progressPhotos) return [];
    return formatPhotosForDisplay(progressPhotos);
  }, [progressPhotos, formatPhotosForDisplay]);

  // Format starting photos with stable references
  const formattedStartingPhotos = React.useMemo(() => {
    if (!startingPhotos) return [];
    return formatPhotosForDisplay(startingPhotos);
  }, [startingPhotos, formatPhotosForDisplay]);

  const referenceGroupId = React.useMemo(() => {
    if (!formattedStartingPhotos?.length) return null;
    return formattedStartingPhotos[0]?.groupId ?? null;
  }, [formattedStartingPhotos]);

  // Month gallery: selected month only, excluding reference group (shown separately)
  const monthGalleryPhotos = React.useMemo(() => {
    const prog = formattedProgressPhotos || [];
    return prog.filter((p) => p.groupId !== referenceGroupId);
  }, [formattedProgressPhotos, referenceGroupId]);

  // Combined for compare/download/analysis — reference always included
  const photos = React.useMemo(() => {
    const byId = new Map();
    [...monthGalleryPhotos, ...(formattedStartingPhotos || [])].forEach((p) => {
      if (!byId.has(p.id)) byId.set(p.id, p);
    });
    const merged = [...byId.values()];
    return merged.length > 0 ? merged : client?.photos || [];
  }, [monthGalleryPhotos, formattedStartingPhotos, client?.photos]);

  const referenceGroups = React.useMemo(() => {
    if (!formattedStartingPhotos?.length) return [];
    return groupFormattedPhotosIntoGroups(
      formattedStartingPhotos,
      referenceGroupId,
    );
  }, [formattedStartingPhotos, referenceGroupId]);

  const monthGroupedPhotos = React.useMemo(() => {
    if (!monthGalleryPhotos.length) return [];
    return groupFormattedPhotosIntoGroups(monthGalleryPhotos, referenceGroupId);
  }, [monthGalleryPhotos, referenceGroupId]);

  // All groups for navigation/download (reference first, then month newest-first)
  const groupedPhotos = React.useMemo(() => {
    const monthIds = new Set(monthGroupedPhotos.map((g) => g.groupId));
    const refs = referenceGroups.filter((g) => !monthIds.has(g.groupId));
    return [...refs, ...monthGroupedPhotos];
  }, [referenceGroups, monthGroupedPhotos]);

  // Format date with useCallback to prevent unnecessary re-renders
  const getFormattedDate = React.useCallback((date) => {
    if (!date) return "Unknown date";
    return new Date(date).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);

  const handleUploadComplete = React.useCallback(async () => {
    if (!clientId) return;
    const { start, end } = getMonthRangeFromKey(monthKey);
    await Promise.all([
      getProgressPhotos(clientId, { start, end }, 50),
      getStartingProgressPhotos(clientId),
    ]);
    setSnackbarMessage("Progress photos uploaded successfully");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  }, [clientId, monthKey, getProgressPhotos, getStartingProgressPhotos]);

  // Event handlers with useCallback
  const handlePhotoClick = React.useCallback(
    (photo) => {
      setSelectedPhoto(photo);
      setOpenViewer(true);

      // Find the group of the selected photo
      const group = groupedPhotos.find((g) =>
        g.photos.some((p) => p.id === photo.id),
      );

      if (group) {
        setCurrentGroupPhotos(group.photos);
        setCurrentPhotoIndex(group.photos.findIndex((p) => p.id === photo.id));
      }
    },
    [groupedPhotos],
  );

  const handleCompareSelect = React.useCallback((photo) => {
    setSelectedPhotos((prev) => {
      if (prev.includes(photo.id)) {
        return prev.filter((id) => id !== photo.id);
      } else if (prev.length < 2) {
        return [...prev, photo.id];
      }
      return prev;
    });
  }, []);

  const handleSetAsStartingPhoto = React.useCallback(
    async (photo) => {
      await setStartingPhotoGroup(clientId, photo.groupId);
      confirm({
        title: "Photo Set as Reference",
        description:
          "This photo has been set as the reference photo for comparisons.",
        confirmationText: "OK",
        cancellationButtonProps: { style: { display: "none" } },
      });
    },
    [setStartingPhotoGroup, clientId, confirm],
  );

  // Handle deleting a photo group
  const handleDeletePhotoGroup = React.useCallback(
    async (groupId, groupDate) => {
      console.log("🗑️ handleDeletePhotoGroup called", {
        groupId,
        groupDate,
        clientId,
      });
      try {
        // useConfirm resolves on confirm, rejects on cancel
        await confirm({
          title: "Delete Progress Photo Group",
          description: `Are you sure you want to delete all photos from ${getFormattedDate(groupDate)}? This action cannot be undone.`,
          confirmationText: "Delete",
          cancellationText: "Cancel",
          confirmationButtonProps: {
            color: "error",
            variant: "contained",
          },
        });

        console.log("🗑️ User confirmed deletion");

        // If we get here, user confirmed
        console.log("🗑️ Calling deleteProgressPhotos with:", {
          clientId,
          groupId,
        });
        const result = await deleteProgressPhotos(clientId, groupId);
        console.log("🗑️ deleteProgressPhotos result:", result);

        if (result && result.success) {
          const { start, end } = getMonthRangeFromKey(monthKey);
          await Promise.all([
            getProgressPhotos(clientId, { start, end }, 50),
            getStartingProgressPhotos(clientId),
          ]);
          setSnackbarMessage(
            `Successfully deleted ${result.deletedCount || ""} photos`,
          );
          setSnackbarSeverity("success");
        } else {
          setSnackbarMessage(result?.message || "Failed to delete photos");
          setSnackbarSeverity("error");
        }
        setSnackbarOpen(true);
      } catch (error) {
        // If user cancels, error will be thrown but we don't need to show an error message
        if (error && error.toString().includes("cancelled")) {
          console.log("🗑️ User cancelled deletion");
          return; // Don't show error message for cancellation
        }

        console.error("🗑️ Error deleting photo group:", error);
        setSnackbarMessage("Failed to delete photo group");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    },
    [
      deleteProgressPhotos,
      clientId,
      confirm,
      getFormattedDate,
      monthKey,
      getProgressPhotos,
      getStartingProgressPhotos,
    ],
  );

  // Static functions that don't need to be recreated on each render
  const getPhotoTypeColor = React.useCallback((type) => {
    switch (type?.toLowerCase()) {
      case "front":
        return "primary";
      case "side":
        return "secondary";
      case "back":
        return "info";
      default:
        return "default";
    }
  }, []);

  // Calculate photoTypes with proper memoization
  const photoTypes = React.useMemo(() => {
    return photos.reduce((acc, photo) => {
      if (photo.displayName) {
        if (!acc[photo.displayName]) {
          acc[photo.displayName] = {
            count: 1,
            type: photo.type, // Store the type to use for finding in configuration
          };
        } else {
          acc[photo.displayName].count += 1;
        }
      }
      return acc;
    }, {});
  }, [photos]);

  // Order photo types according to configuration index
  const orderedPhotoTypes = React.useMemo(() => {
    // Get configuration from the first photo if available
    const configuration = photos.length > 0 ? photos[0].configuration : null;

    // If no configuration, just return the original entries
    if (!configuration) {
      return Object.entries(photoTypes);
    }

    // Sort entries based on configuration index
    return Object.entries(photoTypes).sort((a, b) => {
      const typeA = a[1].type;
      const typeB = b[1].type;

      const configA = configuration.find((c) => c.id === typeA);
      const configB = configuration.find((c) => c.id === typeB);

      // Default to 999 if the index isn't found to push unsorted items to the end
      const indexA = configA ? configA.index : 999;
      const indexB = configB ? configB.index : 999;

      return indexA - indexB;
    });
  }, [photos, photoTypes]);

  // Calculate recent photos with proper memoization
  const recentPhotos = React.useMemo(() => {
    return [...photos]
      .sort((a, b) => {
        if (!a.date || !b.date) return 0;
        return new Date(b.date) - new Date(a.date);
      })
      .slice(0, 3);
  }, [photos]);

  // Handle opening the compare dialog
  const handleOpenCompareDialog = React.useCallback(() => {
    setCompareMode(false);
    setSelectedPhotos([]);
    setOpenCompareDialog(true);
  }, []);

  // Handle downloading all photos as a zip file
  const handleDownloadAllPhotos = React.useCallback(async () => {
    if (!photos || photos.length === 0) {
      setSnackbarMessage("No photos available to download");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    try {
      setDownloading(true);
      setDownloadProgress(0);

      const zip = new JSZip();
      const clientName =
        client?.firstName && client?.lastName
          ? `${client.firstName}_${client.lastName}`
          : `client_${clientId}`;

      // Check if we have proper groupedPhotos
      if (!groupedPhotos || groupedPhotos.length === 0) {
        setSnackbarMessage("Error: Could not organize photos by date");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        setDownloading(false);
        return;
      }

      // Add a Readme file to explain the contents
      zip.file(
        "README.txt",
        `Progress Photos for ${clientName}\nGenerated: ${new Date().toISOString().split("T")[0]}\n` +
          `\nThis zip file contains progress photos organized in folders by date.\n` +
          `Each folder is named with the date (YYYY-MM-DD) when the photos were taken.`,
      );

      // Calculate total number of photos to track progress
      const totalPhotos = photos.length;
      let processedPhotos = 0;

      // Helper function that works around CORS by creating an image element
      const getImageBlobFromUrlViaBrowser = (imageUrl) => {
        return new Promise((resolve, reject) => {
          // Create a canvas element
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          // Create an image element
          const img = new Image();

          // Important: this prevents security errors
          img.crossOrigin = "anonymous";

          img.onload = function () {
            // Set canvas dimensions to match the image
            canvas.width = img.width;
            canvas.height = img.height;

            // Draw image on canvas
            ctx.drawImage(img, 0, 0);

            // Get blob from canvas
            canvas.toBlob(
              function (blob) {
                if (blob) {
                  resolve(blob);
                } else {
                  reject(new Error("Failed to convert image to blob"));
                }
              },
              "image/jpeg",
              0.95,
            );
          };

          img.onerror = function () {
            // Try direct download as fallback (might still fail with CORS)
            const directDownload = new XMLHttpRequest();
            directDownload.open("GET", imageUrl, true);
            directDownload.responseType = "blob";

            directDownload.onload = function () {
              if (this.status === 200) {
                resolve(this.response);
              } else {
                reject(
                  new Error(`Failed to load image directly: ${this.status}`),
                );
              }
            };

            directDownload.onerror = function () {
              // As a last resort, try downloading via proxy
              const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(imageUrl)}`;

              const proxyDownload = new XMLHttpRequest();
              proxyDownload.open("GET", proxyUrl, true);
              proxyDownload.responseType = "blob";

              proxyDownload.onload = function () {
                if (this.status === 200) {
                  resolve(this.response);
                } else {
                  reject(new Error(`All download methods failed for image`));
                }
              };

              proxyDownload.onerror = function () {
                reject(new Error(`All download methods failed for image`));
              };

              proxyDownload.send();
            };

            directDownload.send();
          };

          // Add a cache-busting parameter to avoid cached responses
          img.src = `${imageUrl}${imageUrl.includes("?") ? "&" : "?"}cb=${new Date().getTime()}`;
        });
      };

      // Process photos group by group
      for (const group of groupedPhotos) {
        // Create folder name based on photo date (YYYY-MM-DD)
        const groupDate = new Date(group.date);
        const folderName = groupDate.toISOString().split("T")[0];
        const folder = zip.folder(folderName);

        if (!folder) {
          console.error(`Failed to create folder: ${folderName}`);
          continue;
        }

        // Process each photo in the group
        for (const photo of group.photos) {
          try {
            console.log(`Processing photo: ${photo.displayName || photo.type}`);

            // Try to get image with our more robust method
            const imageBlob = await getImageBlobFromUrlViaBrowser(photo.url);

            // Create a meaningful filename
            const photoType = photo.displayName || photo.type;
            const fileName = `${photoType.replace(/\s+/g, "_")}.jpg`;

            // Add the blob to the zip file
            folder.file(fileName, imageBlob);

            // Update progress
            processedPhotos++;
            setDownloadProgress(
              Math.round((processedPhotos / totalPhotos) * 100),
            );
          } catch (error) {
            console.error(
              `Error processing photo: ${error.message}, URL: ${photo.url}`,
            );
          }
        }
      }

      // Check if we actually added any files
      let fileCount = 0;
      zip.forEach(() => {
        fileCount++;
      });

      if (fileCount <= 1) {
        // Only README.txt
        setSnackbarMessage(
          "Failed to add photos to zip file. Check console for errors.",
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        setDownloading(false);
        return;
      }

      // Generate the zip file
      const content = await zip.generateAsync(
        {
          type: "blob",
          compression: "DEFLATE",
          compressionOptions: { level: 6 },
        },
        (metadata) => {
          setDownloadProgress(Math.round(metadata.percent));
        },
      );

      // Use current date for the filename
      const today = new Date().toISOString().split("T")[0];
      const zipFilename = `${clientName}_progress_photos_${today}.zip`;

      // Save the zip file
      saveAs(content, zipFilename);

      setSnackbarMessage("Photos downloaded successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error downloading photos:", error);
      setSnackbarMessage(`Error downloading photos: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setDownloading(false);
      setDownloadProgress(0);
    }
  }, [photos, groupedPhotos, client, clientId]);

  // Handle snackbar close
  const handleSnackbarClose = React.useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  // Handle navigating to the next photo in the group
  const handleNextPhoto = React.useCallback(() => {
    if (currentPhotoIndex < currentGroupPhotos.length - 1) {
      setCurrentPhotoIndex((prevIndex) => prevIndex + 1);
      setSelectedPhoto(currentGroupPhotos[currentPhotoIndex + 1]);
    }
  }, [currentPhotoIndex, currentGroupPhotos]);

  // Handle navigating to the previous photo in the group
  const handlePreviousPhoto = React.useCallback(() => {
    if (currentPhotoIndex > 0) {
      setCurrentPhotoIndex((prevIndex) => prevIndex - 1);
      setSelectedPhoto(currentGroupPhotos[currentPhotoIndex - 1]);
    }
  }, [currentPhotoIndex, currentGroupPhotos]);

  // Photo analysis handlers
  const handleAnalyzePhotos = React.useCallback(async () => {
    if (!clientId) {
      setAnalysisError("Client ID is required");
      return;
    }

    try {
      setAnalysisError(null);
      const result = await photoAnalysis.triggerAnalysis();
      if (result == null) return;

      setShowAnalysisReport(true);

      setSnackbarMessage("Photo analysis completed successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Photo analysis error:", error);
      setAnalysisError(error.message);

      setSnackbarMessage(error.message);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [clientId, photoAnalysis]);

  const handleCloseAnalysisReport = React.useCallback(() => {
    setShowAnalysisReport(false);
    setAnalysisError(null);
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          ...(embedded ? {} : { p: 3 }),
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <Typography>Loading photos...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={embedded ? { width: "100%" } : { p: 3, width: "100%" }}>
      <Grid container spacing={3}>
        {/* Photo Statistics */}
        <Grid item xs={12}>
          <StyledPhotosCard>
            <PhotosHeader>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <PhotoIcon color="primary" sx={{ fontSize: 28 }} />
                <Typography variant="h6" fontWeight="bold">
                  Photo Progress
                </Typography>
              </Stack>
              <IconButton size="small">
                <MoreIcon />
              </IconButton>
            </PhotosHeader>
            <PhotosContent>
              {/* Redesigned layout with better space utilization */}
              <Grid container spacing={2}>
                {/* Left column - Photo Count and Latest Photo */}
                <Grid item xs={12} sm={4}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box>
                      <Typography
                        variant="h2"
                        fontWeight="bold"
                        color="primary"
                      >
                        {photos.length}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        Total Photos
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Latest:{" "}
                        {recentPhotos[0]?.date
                          ? new Date(recentPhotos[0].date).toLocaleDateString()
                          : "No photos yet"}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>

                {/* Middle column - Photo Types Breakdown */}
                <Grid item xs={12} sm={5}>
                  {photos.length > 0 ? (
                    <Box>
                      <Typography
                        variant="body1"
                        fontWeight="medium"
                        sx={{ mb: 1 }}
                      >
                        Photo Types
                      </Typography>
                      <Stack direction="row" spacing={2} flexWrap="wrap">
                        {orderedPhotoTypes.map(([type, { count }]) => (
                          <Chip
                            key={type}
                            label={`${type.charAt(0).toUpperCase() + type.slice(1)}: ${count}`}
                            color={getPhotoTypeColor(type)}
                            size="medium"
                          />
                        ))}
                      </Stack>
                    </Box>
                  ) : (
                    <Box sx={{ py: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        No photos uploaded yet
                      </Typography>
                    </Box>
                  )}
                </Grid>

                {/* Right column - Action Buttons */}
                <Grid item xs={12} sm={3}>
                  <Stack spacing={1}>
                    <ActionButton
                      variant="contained"
                      color="secondary"
                      startIcon={<UploadIcon />}
                      fullWidth
                      onClick={() => setUploadDialogOpen(true)}
                    >
                      Upload photos
                    </ActionButton>

                    <ActionButton
                      variant="contained"
                      startIcon={<CompareIcon />}
                      fullWidth
                      onClick={handleOpenCompareDialog}
                      disabled={groupedPhotos.length < 2}
                    >
                      Compare groups
                    </ActionButton>

                    {/* Download Button with Progress */}
                    {downloading ? (
                      <Box sx={{ width: "100%", position: "relative", mb: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={downloadProgress}
                          sx={{ height: 36, borderRadius: 18 }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontWeight="medium"
                          >
                            {downloadProgress}% Complete
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <ActionButton
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        fullWidth
                        onClick={handleDownloadAllPhotos}
                        disabled={photos.length === 0}
                      >
                        Download All
                      </ActionButton>
                    )}
                  </Stack>
                </Grid>

                {/* Progress Summary - Shows across the bottom */}
                {photos.length > 0 && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        mt: 1,
                        pt: 2,
                        borderTop: 1,
                        borderColor: "divider",
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="body2" color="text.secondary">
                          First photo:{" "}
                          {groupedPhotos.length > 0 &&
                          groupedPhotos[groupedPhotos.length - 1]?.date
                            ? getFormattedDate(
                                groupedPhotos[groupedPhotos.length - 1].date,
                              )
                            : "N/A"}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          color="primary"
                        >
                          {groupedPhotos.length} progress updates
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Latest:{" "}
                          {groupedPhotos[0]?.date
                            ? getFormattedDate(groupedPhotos[0].date)
                            : "N/A"}
                        </Typography>
                      </Stack>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </PhotosContent>
          </StyledPhotosCard>
        </Grid>

        {/* Photo Gallery */}
        <Grid item xs={12}>
          <StyledPhotosCard>
            <PhotosHeader>
              <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
                <ProgressIcon color="primary" sx={{ fontSize: 28 }} />
                <Typography variant="h6" fontWeight="bold">
                  Progress Gallery
                </Typography>
                <MonthYearPicker
                  value={monthKey}
                  onChange={setMonthKey}
                  showToday
                />
                {compareMode && (
                  <Chip
                    label={`${selectedPhotos.length}/2 selected`}
                    color="primary"
                    size="small"
                  />
                )}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>
                {!compareMode && photos.length > 0 && (
                  canAnalyzePhotos ? (
                  <Stack spacing={0.75} sx={{ minWidth: { xs: "100%", sm: "auto" } }}>
                    <AIButton
                      loading={photoAnalysis.isAnalyzing}
                      loadingText="Generating…"
                      onClick={handleAnalyzePhotos}
                      disabled={!photoAnalysis.canAnalyze()}
                    >
                      AI Analysis
                    </AIButton>
                    {photoAnalysis.isAnalyzing ? (
                      <>
                        <LinearProgress
                          color="primary"
                          sx={{ borderRadius: 1, height: 4, maxWidth: 280 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          Generating analysis — this can take a minute…
                        </Typography>
                      </>
                    ) : null}
                  </Stack>
                  ) : (
                    <UpgradePrompt
                      organizationData={organizationData}
                      minTier="pro"
                      variant="banner"
                      title="Upgrade for AI photo analysis"
                      description="Progress photo AI analysis is available on Pro."
                      featureName="ai_progress_photo_analysis"
                    />
                  )
                )}
                {canAnalyzePhotos && !photoAnalysis.canAnalyze() && !photoAnalysis.isAnalyzing && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ px: 1, alignSelf: "center" }}
                  >
                    Next analysis in {photoAnalysis.formatTimeRemaining()}
                  </Typography>
                )}
                {compareMode && (
                  <ActionButton
                    variant="contained"
                    disabled={selectedPhotos.length !== 2}
                    onClick={() => {
                      // Handle compare action - could open a comparison dialog
                      const selectedPhotoObjects = photos.filter((photo) =>
                        selectedPhotos.includes(photo.id),
                      );
                      console.log("Compare photos:", selectedPhotoObjects);
                    }}
                  >
                    Compare Selected
                  </ActionButton>
                )}
                {compareMode && (
                  <ActionButton
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setCompareMode(false);
                      setSelectedPhotos([]);
                    }}
                  >
                    Cancel
                  </ActionButton>
                )}
              </Stack>
            </PhotosHeader>
            <PhotosContent>
              {referenceGroups.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{ mb: 2 }}
                  >
                    Reference photos
                  </Typography>
                  <Stack spacing={3}>
                    {referenceGroups.map((group) => (
                      <Box key={`ref-${group.groupId}`}>
                        <ProgressPhotoGroupPanel
                          group={group}
                          clientId={clientId}
                          isReferenceGroup
                          requireReveal={false}
                          showHeaderActions
                          onSetReference={(gid) =>
                            handleSetAsStartingPhoto({ groupId: gid })
                          }
                          onDeleteGroup={handleDeletePhotoGroup}
                          onPhotoClick={(photo) => {
                            if (compareMode) {
                              handleCompareSelect(photo);
                            } else {
                              handlePhotoClick(photo);
                            }
                          }}
                          photoCardSx={(photo) => ({
                            border:
                              compareMode && selectedPhotos.includes(photo.id)
                                ? "3px solid"
                                : `2px solid ${NewColors.charts.blue}`,
                            borderColor:
                              compareMode && selectedPhotos.includes(photo.id)
                                ? "primary.main"
                                : undefined,
                            boxShadow: "0 0 8px rgba(33,150,243,0.5)",
                          })}
                        />
                      </Box>
                    ))}
                  </Stack>
                  <Divider sx={{ mt: 3 }} />
                </Box>
              )}

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : monthGroupedPhotos.length > 0 ? (
                <Stack spacing={4}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Photos this month
                  </Typography>
                  {monthGroupedPhotos.map((group) => (
                    <Box key={group.groupId}>
                      <ProgressPhotoGroupPanel
                        group={group}
                        clientId={clientId}
                        isReferenceGroup={
                          Boolean(referenceGroupId) &&
                          referenceGroupId === group.groupId
                        }
                        requireReveal={false}
                        showHeaderActions
                        onSetReference={(gid) =>
                          handleSetAsStartingPhoto({ groupId: gid })
                        }
                        onDeleteGroup={handleDeletePhotoGroup}
                        onPhotoClick={(photo) => {
                          if (compareMode) {
                            handleCompareSelect(photo);
                          } else {
                            handlePhotoClick(photo);
                          }
                        }}
                        photoCardSx={(photo) => ({
                          border:
                            compareMode && selectedPhotos.includes(photo.id)
                              ? "3px solid"
                              : "none",
                          borderColor:
                            compareMode && selectedPhotos.includes(photo.id)
                              ? "primary.main"
                              : undefined,
                        })}
                      />
                      <Divider sx={{ mt: 3 }} />
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Paper
                  sx={{
                    p: 3,
                    textAlign: "center",
                    bgcolor: "grey.50",
                    borderStyle: "dashed",
                    borderWidth: 2,
                    borderColor: "grey.300",
                  }}
                >
                  <PhotoIcon sx={{ fontSize: 48, color: "grey.400", mb: 2 }} />
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {referenceGroups.length > 0
                      ? "No progress photos in this month"
                      : "No progress photos yet"}
                  </Typography>
                </Paper>
              )}
            </PhotosContent>
          </StyledPhotosCard>
        </Grid>
      </Grid>

      {/* Photo Viewer Dialog */}
      <Dialog
        open={openViewer}
        onClose={() => setOpenViewer(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedPhoto && (
          <>
            <DialogContent sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Box sx={{ textAlign: "center" }}>
                  <img
                    src={selectedPhoto.url}
                    alt={`Progress photo - ${selectedPhoto.type}`}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "70vh",
                      borderRadius: 8,
                    }}
                  />
                </Box>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      {selectedPhoto.displayName} View
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedPhoto.date instanceof Date
                        ? selectedPhoto.date.toLocaleDateString()
                        : new Date(selectedPhoto.date).toLocaleDateString()}
                    </Typography>
                    {selectedPhoto.weight && (
                      <Typography variant="body2">
                        Weight: {selectedPhoto.weight} lbs
                      </Typography>
                    )}
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      onClick={handlePreviousPhoto}
                      disabled={currentPhotoIndex === 0}
                    >
                      <PrevIcon />
                    </IconButton>
                    <IconButton
                      onClick={handleNextPhoto}
                      disabled={
                        currentPhotoIndex === currentGroupPhotos.length - 1
                      }
                    >
                      <NextIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenViewer(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <ProgressPhotoCompareAnyTwoDialog
        open={openCompareDialog}
        onClose={() => setOpenCompareDialog(false)}
        groupedPhotos={groupedPhotos}
        referenceGroupId={referenceGroupId}
      />

      {/* Photo Analysis Report Dialog */}
      <Dialog
        open={showAnalysisReport}
        onClose={handleCloseAnalysisReport}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: "95vw",
            height: "90vh",
            maxWidth: "1200px",
            maxHeight: "900px",
            borderRadius: 3,
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <PhotoAnalysisReport
            analysisData={photoAnalysis.data?.data}
            clientName={
              `${client?.firstName || ""} ${client?.lastName || ""}`.trim() ||
              "Client"
            }
            onClose={handleCloseAnalysisReport}
          />
        </Box>
      </Dialog>

      {/* Snackbar for download status */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <ProgressPhotoUploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        clientId={clientId}
        organizationId={organizationId}
        progressPhotoConfig={progressPhotoConfig}
        hasStartingPhotos={Boolean(referenceGroupId)}
        onUploaded={handleUploadComplete}
      />
    </Box>
  );
};

/**
 * @param {object} props
 * @param {boolean} [props.embedded] — weekly report layout (no outer padding)
 * @param {boolean} [props.requireReveal=true] — hide photos until coach clicks reveal
 */
const EnhancedPhotosTab = ({
  requireReveal = true,
  embedded = false,
  ...props
}) => {
  const clientId = props.client?.uid;

  if (requireReveal) {
    return (
      <Box sx={embedded ? { width: "100%" } : { p: 3, width: "100%" }}>
        <ProgressPhotosPrivacyGate resetKey={clientId}>
          <EnhancedPhotosTabContent
            {...props}
            embedded={embedded}
          />
        </ProgressPhotosPrivacyGate>
      </Box>
    );
  }

  return <EnhancedPhotosTabContent {...props} embedded={embedded} />;
};

export default EnhancedPhotosTab;
